<script setup lang="ts">
import { ref, watch } from "vue";
import { v4 as uuidv4 } from "uuid";
import { useDragAndDrop } from "@/ui/composables/useDragAndDrop";
import {
	Rule,
	RuleSetRule,
	isRuleSetRule,
	createConditionRule,
	createClampVariableRule,
	createLoadMyWheelsBasePriceRule,
} from "@/modules/rule_engine/ruleEngineClasses";
import {
	displayComponentFor,
	editComponentFor,
	CreatableRuleType,
} from "@/modules/rule_engine/ruleTypeRegistry";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";
import { toRawDeep } from "@/Utils/to-raw-deep";

/**
 * Renders and edits one level of rules.
 *
 * Deliberately knows nothing about model versions, groups above or below it,
 * routing, or saving — the surrounding shell owns all of that. `rules` is
 * mutated in place because it is the array held by the store.
 */
const rules = defineModel<Rule[]>({ required: true });

withDefaults(
	defineProps<{
		allowedRuleTypes?: CreatableRuleType[];
		/** Render selection checkboxes on each rule. */
		selectable?: boolean;
		/** Shell-controlled, e.g. while a bulk selection is active. */
		addDisabled?: boolean;
	}>(),
	{
		allowedRuleTypes: () => [
			"condition",
			"clamp_variable",
			"load_my_wheels_base_price",
		],
		selectable: false,
		addDisabled: false,
	}
);

const emit = defineEmits<{ "navigate-to-group": [rule: RuleSetRule] }>();

const dragAndDrop = ref<ReturnType<typeof useDragAndDrop>>();

watch(
	rules,
	(currentRules) => {
		if (currentRules) dragAndDrop.value = useDragAndDrop(currentRules);
	},
	{ immediate: true }
);

const ruleToEdit = ref<Rule | null>(null);
const isEditRuleVisible = ref<boolean>(false);

const editRule = (rule: Rule) => {
	// Groups have no edit dialog — the shell navigates into them instead.
	if (isRuleSetRule(rule)) {
		emit("navigate-to-group", rule);
		return;
	}

	ruleToEdit.value = rule;
	isEditRuleVisible.value = true;
};

const storeEditedRule = (rule: Rule) => {
	const index = rules.value.findIndex(
		(existing) => existing.uuid === rule.uuid
	);

	if (index !== -1) {
		rules.value.splice(index, 1, rule);
	} else {
		rules.value.push(rule);
	}
};

/**
 * Gives every `uuid` in a cloned rule a fresh value, in place.
 *
 * `dimension` is skipped — its uuid identifies a rule dimension owned by the
 * backend, not a node in the model, so it must be preserved.
 */
const assignFreshUuids = (value: unknown): void => {
	if (Array.isArray(value)) {
		value.forEach(assignFreshUuids);
		return;
	}

	if (value === null || typeof value !== "object") return;

	const node = value as Record<string, unknown>;

	for (const key of Object.keys(node)) {
		if (key === "dimension") continue;

		if (key === "uuid") {
			node[key] = uuidv4();
			continue;
		}

		assignFreshUuids(node[key]);
	}
};

const cloneRule = (rule: Rule) => {
	const clonedRule = toRawDeep(rule);

	assignFreshUuids(clonedRule);

	rules.value.push(clonedRule);
};

/** No confirmation: nothing is persisted until save, and undo restores it. */
const deleteRule = (uuid: string) => {
	const index = rules.value.findIndex((rule) => rule.uuid === uuid);
	if (index !== -1) {
		rules.value.splice(index, 1);
	}

	// clear selection to prevent ui from being stuck
	useRuleEnginedStore().ruleEngineState.selectedRules = [];
};

/**
 * Publishes which rules a drag carries, so a drop target outside this list (the
 * group tree) can move them. `useDragAndDrop` puts the index in `text/plain`
 * for within-level reordering; this is a second, additive payload.
 *
 * Dragging a rule that is part of the current selection drags the whole
 * selection, which is the behaviour people expect from a file manager.
 */
const publishDraggedRules = (event: DragEvent, rule: Rule) => {
	const selected = useRuleEnginedStore().ruleEngineState.selectedRules;
	const uuids = selected.includes(rule.uuid) ? [...selected] : [rule.uuid];

	event.dataTransfer?.setData(
		"application/x-rule-uuids",
		JSON.stringify(uuids)
	);
};

const addConditionRule = () => editRule(createConditionRule(uuidv4()));

const addClampVariableRule = () =>
	editRule(
		createClampVariableRule(
			uuidv4(),
			useRuleEnginedStore()?.availableActionVariables[0]?.name ?? "",
			0,
			10
		)
	);

const addLoadMyWheelsBasePriceRule = () =>
	editRule(createLoadMyWheelsBasePriceRule(uuidv4()));
</script>

<template>
	<component
		v-if="isEditRuleVisible"
		v-model="isEditRuleVisible"
		:is="editComponentFor(ruleToEdit.type)"
		:rule="ruleToEdit"
		@store="storeEditedRule"
	/>

	<div class="d-flex">
		<v-card-title>Rules:</v-card-title>
	</div>

	<template v-if="rules.length > 0">
		<v-card
			draggable="true"
			v-for="(rule, index) in rules"
			@dragstart="
				(event) => {
					dragAndDrop.dragstartHandle(event, index);
					publishDraggedRules(event, rule);
				}
			"
			:key="rule.uuid"
			color="primary"
			:variant="
				useRuleEnginedStore().ruleEngineState.selectedRules.includes(rule.uuid)
					? 'outlined'
					: undefined
			"
			:class="'mt-5'"
		>
			<component
				:is="displayComponentFor(rule.type)"
				:rule="rule"
				:index="index"
				:read-only="false"
				:checkable="selectable"
				@clone-rule="cloneRule(rule)"
				@edit-rule="editRule(rule)"
				@delete-rule="deleteRule(rule.uuid)"
			/>

			<span
				class="order-dropzone"
				:data-drag-active="dragAndDrop.isDragActive"
				@dragover="(event) => dragAndDrop.dragoverHandle(event, index)"
				@dragleave="(event) => dragAndDrop.dragleaveHandle(event)"
				@drop="(event) => dragAndDrop.dragdropHandle(event, index)"
			></span>
		</v-card>
	</template>
	<template v-else>
		<v-card class="pa-5">
			<i>
				There are currently no rules configured, click 'Add rule' to start.
			</i>
		</v-card>
	</template>

	<div class="my-5 d-flex">
		<v-spacer />
		<v-menu transition="slide-y-transition">
			<template v-slot:activator="{ props: menuProps }">
				<v-btn
					:disabled="addDisabled"
					color="primary"
					variant="tonal"
					v-bind="menuProps"
				>
					Add rule
				</v-btn>
			</template>
			<v-list density="compact">
				<v-list-item
					v-if="allowedRuleTypes.includes('condition')"
					@click="addConditionRule"
				>
					<template #prepend>
						<v-icon size="20">fa: fa-light fa-function</v-icon>
					</template>
					<v-list-item-title>Conditional Rule</v-list-item-title>
				</v-list-item>
				<v-list-item
					v-if="allowedRuleTypes.includes('clamp_variable')"
					@click="addClampVariableRule"
				>
					<template #prepend>
						<v-icon size="20">fa: fa-light fa-arrows-to-line</v-icon>
					</template>
					<v-list-item-title>Clamp Variable Rule</v-list-item-title>
				</v-list-item>
				<v-list-item
					v-if="allowedRuleTypes.includes('load_my_wheels_base_price')"
					@click="addLoadMyWheelsBasePriceRule"
				>
					<template #prepend>
						<v-icon size="20">fa: fa-light fa-car-garage</v-icon>
					</template>
					<v-list-item-title>MyWheels Base Price Rule </v-list-item-title>
				</v-list-item>
			</v-list>
		</v-menu>
		<slot name="bulk-actions" />
		<v-spacer />
	</div>
</template>

<style lang="scss" scoped>
.order-dropzone {
	position: absolute;
	bottom: -0.5rem;
	top: 0;
	left: 0;
	z-index: 100;
	transition: all 125ms ease;

	&[data-drag-active="true"] {
		width: 100%;
		height: 100%;
	}

	&[data-drag-active="false"] {
		height: 0;
		width: 0;
	}

	&.over {
		background: rgba(255, 152, 0, 0.2);
	}
}
</style>
